// utility.ts

import { toast } from "react-toastify";
import {
  IMAGE_BASE_PATH,
  SLIDES_DELAY_KEY,
} from "../api/apiConfig";
import { Setting } from "../features/settingsSlice";
import { AppDispatch } from "../app/store";
import { NavigateFunction } from "react-router-dom";
import { handlePageSelection } from "../features/pageSlice";
import { Box, Item } from "./boxTypes";
import { VpcPrice } from "../features/walletSlice";
import axios from "axios";


export const getLowestStockCount = (box: Box): number => {
  if (!box.items || box.items.length === 0) {
    return 0;
  }

  const validItems = box.items.filter((item) => item.detail !== null);

  const lowestStockCount = validItems.reduce((min, item) => {
    if (item.detail && item.detail.stock_count < min) {
      return item.detail.stock_count;
    }
    return min;
  }, Infinity);

  return lowestStockCount === Infinity ? 0 : lowestStockCount;
}

export const updateBoxItemsStockCount = (box: Box, updatedItems: Item[]): Box => {
  // Deep clone the box and its items
  const updatedBox: Box = {
    ...box,
    items: box.items.map((boxItem) => {
      // Calculate the count of items in updatedItems with the same item_id
      const sameIdCount = updatedItems.filter((updatedItem) => updatedItem.item_id === boxItem.item_id).length;

      // If the count is greater than 0, subtract it from the box item's stock_count
      if (boxItem.detail && sameIdCount > 0) {
        return {
          ...boxItem,
          detail: {
            ...boxItem.detail,
            stock_count: boxItem.detail.stock_count - sameIdCount, // Subtract the count of same items
          },
        };
      }

      // Return the box item unchanged if no matching items
      return { ...boxItem };
    }),
  };

  return updatedBox;
};


export const getAvatarImagePath = (): string => {
  return "/assets/Female_2_s.png";
};


export const getImagePath = (pic: string): string => {
  return `${IMAGE_BASE_PATH}${pic}`;
};

export const getVideoPath = (video: string | null): string | null => {
  if (!video) {
    return null;
  }
  return `${IMAGE_BASE_PATH}${video}`;
};



export const getImageSource = (pic?: string | null): string => {
  if (!pic) return "/assets/default-box.png";

  const cleanPic = pic.split("?")[0]; // remove query params

  if (/\.(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(cleanPic)) {
    return getImagePath(pic);
  }

  return "/assets/default-box.png";
};

// Other utility functions can be added here if needed

export const showToast = (message: string, options?: object) => {
  toast(message, options);
};

export function formatNumber(value?: number): string {
  if (value === undefined || value === null) {
    return "0"; // or return any default value or placeholder
  }
  return value % 1 === 0 ? value.toString() : value.toFixed(2);
}

export const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const formatCurrencyWithoutDecimal = (amount: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};


export const formatCurrencyWithSymbol = (amount: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

// Utility function to get the "default_box_surcharge" setting, default to 15 if not found
export const getDefaultBoxSlidesDelay = (settings: Setting[]): number => {
  const slidesDelay = settings.find(
    (setting) => setting.setting === SLIDES_DELAY_KEY
  );
  return slidesDelay ? parseFloat(slidesDelay.value) : 7000; // Return the value as a number or default 15
};

export const handleLogoutUser = (
  dispatch: AppDispatch,
  navigate: NavigateFunction
) => {
  showToast("Session expired!", {
    autoClose: 3000, // Auto-close the toast after 3000 milliseconds (3 seconds)
    // Other options can be added here
  });

  localStorage.clear();
  window.location.href = "/";
  sessionStorage.clear(); // This will clear all sessionStorage keys
  dispatch(handlePageSelection("home"));
  navigate(`/`);
};

// Utility function to get the "default_box_surcharge" setting, default to 15 if not found
export const getHotBoxesLimit = (settings: Setting[]): number => {
  const hot_boxes_limit = settings.find((setting) => setting.setting === "hot_boxes_limit");
  return hot_boxes_limit ? parseInt(hot_boxes_limit.value) : 20; // Return the value as a number or default 20
};


export const getConversionRate = (currency: 'usdc' | 'vpc' | 'solana', vpcPrice: any) => {
  if (!vpcPrice) return 1; // Default conversion rate for USDT

  switch (currency) {
    case 'usdc':
      return 1;
    case 'vpc':
      return vpcPrice.usdt_vpc.cost / vpcPrice.usdt_vpc.amount;
    case 'solana':
      return vpcPrice.sol_usdt.amount / vpcPrice.sol_usdt.cost;
    default:
      return 1;
  }
};

export const convertVpcUsdc = (amount: number, fromCurrency: 'usdc' | 'vpc', vpcPrice: VpcPrice | null) => {
  if (!vpcPrice || amount <= 0) return 0; // Default to 0 if no price data or invalid amount

  const rate = vpcPrice.usdt_vpc.cost / vpcPrice.usdt_vpc.amount; // USDC to VPC rate

  if (fromCurrency === 'usdc') {
    return amount / rate; // Convert USDC to VPC
  } else if (fromCurrency === 'vpc') {
    return amount * rate; // Convert VPC to USDC
  }

  return 0;
};


export function getPrice(box: Box | null, vpcPrice:VpcPrice | null) {
  if(box == null){
    return "";
  }
    if (box.vpc_enabled) {
      if (!vpcPrice?.usdt_vpc?.amount) {
        return "Loading..."; // Show a fallback if VPC price isn't available
      }
      return `${formatCurrency(box.final_value * vpcPrice.usdt_vpc.amount)} VPC`;
    }
    return `$${formatCurrency(box.final_value )}`;
  }

  // Axios instance with authorization
export const apiForSSO = axios.create({
  baseURL: process.env.REACT_APP_API_SSO_BASE_URL!,
  headers: { Accept: "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
});